import { useMutation } from '@tanstack/react-query';
import { useLocation, useNavigate } from 'react-router';

import { createApiClient } from '@/api/client';
import type { ApiError, LegacyKuduResponseEnvelope } from '@/api/client.types';
import { createLegacyKuduToken, getKuduUserId } from '@/api/legacyKuduAuth';
import { useAuth } from '@/auth/useAuth';
import { createGradebookBatchRanges } from '@/features/gradebook/gradebookService';
import type { ComponentPageData } from '@/features/content/useComponent';
import type { NormalizedQuestionData } from '@/features/questions/questionNormalizer';

import {
  exportDetailedScoresCsv,
  type DetailedCsvQuestion,
  type DetailedCsvStudent,
  type RawDetailedQuestionStat,
} from './detailedScoresCsv';
import { statsFailure } from './statsFailure';

export interface DownloadDetailedScoresInput {
  component: ComponentPageData;
  componentId: string;
  courseId: string;
}

function createDataError(message: string): ApiError {
  const error = new Error(message) as ApiError;
  error.kind = 'data';
  return error;
}

// Legacy Kudu responses may be wrapped in a { Response } envelope.
function unwrapEnvelope(response: unknown): Record<string, unknown> | null {
  if (response && typeof response === 'object') {
    if ('Response' in response) {
      return unwrapEnvelope((response as { Response: unknown }).Response);
    }
    return response as Record<string, unknown>;
  }
  return null;
}

function asArray<T>(value: unknown): T[] {
  return Array.isArray(value) ? (value as T[]) : [];
}

function asString(value: unknown): string {
  return typeof value === 'string' ? value : '';
}

// Map a normalized homework question to the CSV question model, capturing what the
// answer/solution formatters need. `index` is the question's position within the
// section's question_stats (used to line raw student stats up with the question).
// Parts are indexed by their position within the parent so a student's
// question_set_stats line up positionally.
function toDetailedQuestion(
  question: NormalizedQuestionData,
  index: number,
  label: string,
): DetailedCsvQuestion {
  const base = {
    index,
    isAiUpload: question.kind === 'aiUpload',
    kind: question.kind,
    label,
    maxScore: question.pointsPossible,
    usesRandomVariables: question.usesRandomVariables,
  } satisfies Omit<DetailedCsvQuestion, 'correctAnswer' | 'correctAnswerIndex' | 'parts'>;

  if (question.kind === 'multipleChoice') {
    return { ...base, correctAnswerIndex: question.correctAnswerIndex };
  }
  if (question.kind === 'selectAll') {
    return { ...base, correctAnswerIndices: question.correctAnswerIndices };
  }
  if (question.kind === 'numerical') {
    return {
      ...base,
      correctAnswer: question.correctAnswer,
      correctAnswerExtraExponent: question.correctAnswerExtraExponent,
    };
  }
  if (question.kind === 'exactAnswer') {
    return { ...base, correctAnswer: question.correctAnswer };
  }
  if (question.kind === 'multiPart') {
    return {
      ...base,
      parts: question.parts.map((part, partIndex) =>
        toDetailedQuestion(part, partIndex, `${label}${String.fromCharCode(97 + partIndex)}`),
      ),
    };
  }
  return base;
}

// Build the exported question list: assigned homework questions, numbered Q1..Qn.
// The raw-stat index is the question's ORIGINAL position among the homework
// questions (unassigned questions still occupy a position in question_stats), so
// it is tracked before filtering to assigned.
export function buildDetailedQuestions(
  component: ComponentPageData,
): DetailedCsvQuestion[] {
  const homeworkQuestions = component.questionSections
    .filter((section) => section.kind === 'homework')
    .flatMap((section) => section.questions);

  const questions: DetailedCsvQuestion[] = [];
  let assignedNumber = 0;
  homeworkQuestions.forEach((question, position) => {
    if (!question.isAssigned) {
      return;
    }
    assignedNumber += 1;
    questions.push(toDetailedQuestion(question, position, `Q${assignedNumber}`));
  });
  return questions;
}

function readQuestionStatsForComponent(
  rawStudent: Record<string, unknown>,
  componentId: string,
): RawDetailedQuestionStat[] {
  const components = asArray<Record<string, unknown>>(rawStudent.components);
  const target =
    components.find((comp) => asString(comp.uid ?? comp.id) === componentId) ??
    components[0];
  const sectionStats = asArray<Record<string, unknown>>(
    target?.section_stats ?? target?.sectionStats,
  );
  const firstSection = sectionStats[0];
  return asArray<RawDetailedQuestionStat>(
    firstSection?.question_stats ?? firstSection?.questionStats,
  );
}

function toDetailedStudent(
  rawStudent: Record<string, unknown>,
  componentId: string,
): DetailedCsvStudent {
  const profile = (rawStudent.profile ?? {}) as Record<string, unknown>;
  return {
    email: asString(profile.preferred_email ?? profile.preferredEmail),
    firstName: asString(profile.first_name ?? profile.firstName),
    instituteId: asString(
      profile.institute_official_id ?? profile.instituteOfficialId,
    ),
    lastName: asString(profile.last_name ?? profile.lastName),
    questionStats: readQuestionStatsForComponent(rawStudent, componentId),
  };
}

function compareStudents(a: DetailedCsvStudent, b: DetailedCsvStudent): number {
  const last = a.lastName.trim().toLowerCase().localeCompare(b.lastName.trim().toLowerCase());
  if (last !== 0) {
    return last;
  }
  return a.firstName.trim().toLowerCase().localeCompare(b.firstName.trim().toLowerCase());
}

/**
 * Downloads a detailed per-question CSV of every student's homework interaction
 * stats (score, answer, solution, attempts, AI hints, AI practice, needs-grading,
 * and AI feedback) for one component. Students are fetched in batches (like the
 * roster) so large classes are handled, then sorted by name and written to a CSV.
 */
export function useDownloadDetailedScores() {
  const location = useLocation();
  const navigate = useNavigate();
  const { getIdToken, user } = useAuth();

  return useMutation<void, ApiError, DownloadDetailedScoresInput>({
    mutationFn: async ({ component, componentId, courseId }) => {
      if (!user) {
        throw createDataError('You must be signed in to download scores.');
      }
      const questions = buildDetailedQuestions(component);
      if (questions.length === 0) {
        throw createDataError(
          'This assignment has no assigned homework questions to export.',
        );
      }
      const kuduUserId = getKuduUserId(user) ?? user.uid;
      if (!kuduUserId) {
        throw createDataError('Unable to determine the current Kudu user ID.');
      }

      const returnTo = `${location.pathname}${location.search}${location.hash}`;
      const client = createApiClient({
        baseUrl: import.meta.env.VITE_API_BASE_URL || '',
        getIdToken,
        getLegacyToken: async () => createLegacyKuduToken(user),
        onAuthError: () =>
          navigate(`/login?returnTo=${encodeURIComponent(returnTo)}`, {
            replace: true,
          }),
      });

      const rawStudents: Record<string, unknown>[] = [];
      try {
        // How many students are enrolled (to size the batches).
        const groupResponse = await client.invokeKuduMethod<unknown, {
          course_id: string;
          is_instructor_request: boolean;
          user_id: string;
        }>('GetCourseStudentGroup', {
          course_id: courseId,
          is_instructor_request: true,
          user_id: kuduUserId,
        });
        const group = (unwrapEnvelope(groupResponse)?.course_student_group ??
          unwrapEnvelope(groupResponse)?.courseStudentGroup ??
          {}) as Record<string, unknown>;
        const studentIds =
          group.course_student_ids ?? group.courseStudentIds ?? group.course_students;
        const studentCount = Array.isArray(studentIds) ? studentIds.length : 0;

        // Fetch each batch's students (profiles + this component's raw section_stats).
        for (const range of createGradebookBatchRanges(studentCount)) {
          const batchResponse = await client.invokeKuduMethod<unknown, {
            component_ids: string[];
            course_id: string;
            end_index: number;
            start_index: number;
          }>('GetCourseStudents', {
            component_ids: [componentId],
            course_id: courseId,
            end_index: range.endIndex,
            start_index: range.startIndex,
          });
          const payload = unwrapEnvelope(batchResponse);
          rawStudents.push(
            ...asArray<Record<string, unknown>>(
              payload?.course_students ?? payload?.courseStudents,
            ),
          );
        }
      } catch (error) {
        throw statsFailure(error, 'The scores were not downloaded.');
      }

      const students = rawStudents
        .map((rawStudent) => toDetailedStudent(rawStudent, componentId))
        .sort(compareStudents);

      exportDetailedScoresCsv(component.title, questions, students);
    },
  });
}
